import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { HandCoins, Check, X, RotateCcw, ShoppingBag, ArrowRight } from "lucide-react";
import { toast } from "sonner";

const fmt = (cents) => `$${((cents || 0) / 100).toFixed(2)}`;

const STATUS_META = {
  pending:   { label: "Pending",     className: "bg-amber-100 text-amber-800 border-amber-200" },
  accepted:  { label: "Accepted",    className: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  rejected:  { label: "Rejected",    className: "bg-red-100 text-red-800 border-red-200" },
  countered: { label: "Countered",   className: "bg-blue-100 text-blue-800 border-blue-200" },
  expired:   { label: "Expired",     className: "bg-gray-100 text-gray-700 border-gray-200" },
  withdrawn: { label: "Withdrawn",   className: "bg-gray-100 text-gray-700 border-gray-200" },
};

export default function MyOffers() {
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState(null);

  const refresh = async () => {
    try {
      const { data } = await api.get("/marketplace/offers/mine");
      setItems(data.items || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setItems([]);
    }
  };

  useEffect(() => { refresh(); }, []);

  const withdraw = async (oid) => {
    setBusy(oid);
    try {
      await api.post(`/marketplace/offers/${oid}/withdraw`);
      toast.success("Offer withdrawn.");
      await refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(null); }
  };

  return (
    <div className="max-w-4xl mx-auto px-6 md:px-10 py-10" data-testid="my-offers-page">
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-xl bg-[#F9E4E1] text-[#B31E1E] flex items-center justify-center">
          <HandCoins size={20} />
        </div>
        <div>
          <h1 className="font-display text-3xl leading-tight">My offers</h1>
          <p className="text-sm text-[#5C4E48]">Offers you&apos;ve sent on marketplace listings.</p>
        </div>
      </div>

      {items === null ? (
        <div className="mt-10 h-48 rounded-xl bg-[#FAF5F2] animate-pulse" />
      ) : items.length === 0 ? (
        <Card className="mt-10 rounded-xl border border-[#EAE3DE] bg-white p-10 text-center" data-testid="my-offers-empty">
          <ShoppingBag size={32} className="mx-auto text-[#B31E1E]" />
          <div className="mt-3 font-semibold text-[#1B1614]">No offers yet</div>
          <p className="mt-1 text-sm text-[#5C4E48]">
            Browse the marketplace and hit &ldquo;Make an offer&rdquo; on listings that accept offers.
          </p>
          <Link to="/marketplace">
            <Button className="mt-5 rounded-lg bg-[#B31E1E] hover:bg-[#8F1717] text-white">
              Browse marketplace <ArrowRight size={14} className="ml-2" />
            </Button>
          </Link>
        </Card>
      ) : (
        <ul className="mt-8 space-y-3">
          {items.map((o) => {
            const meta = STATUS_META[o.status] || STATUS_META.pending;
            const isPending = o.status === "pending" || o.status === "countered";
            return (
              <Card key={o.id} className="rounded-xl border border-[#EAE3DE] bg-white p-4" data-testid={`offer-row-${o.id}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <Link
                      to={`/marketplace/listings/${o.listing_id}`}
                      className="font-semibold text-[#1B1614] hover:text-[#B31E1E] transition-colors truncate block"
                    >
                      {o.listing_title || `Listing ${o.listing_id.slice(0, 8)}`}
                    </Link>
                    <div className="mt-1 flex items-baseline gap-3 text-sm">
                      <span className="text-[#8B7B73]">You offered</span>
                      <span className="text-lg font-black tabular-nums text-[#1B1614]">{fmt(o.amount_cents)}</span>
                    </div>
                    {o.counter_amount_cents && (
                      <div className="mt-0.5 flex items-baseline gap-3 text-sm">
                        <span className="text-[#8B7B73]">Seller countered</span>
                        <span className="text-lg font-black tabular-nums text-[#B31E1E]">{fmt(o.counter_amount_cents)}</span>
                      </div>
                    )}
                    {o.message && <p className="mt-2 text-xs text-[#5C4E48] italic">&ldquo;{o.message}&rdquo;</p>}
                    <div className="mt-2 text-[11px] text-[#8B7B73]">
                      Sent {new Date(o.created_at).toLocaleDateString()}
                      {o.expires_at && ` · expires ${new Date(o.expires_at).toLocaleDateString()}`}
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <Badge className={`rounded-md border ${meta.className}`} data-testid={`offer-status-${o.id}`}>{meta.label}</Badge>
                    {o.status === "accepted" && (
                      <Link to={`/marketplace/listings/${o.listing_id}`}>
                        <Button size="sm" className="rounded-md bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`offer-checkout-${o.id}`}>
                          <Check size={13} className="mr-1" /> Checkout
                        </Button>
                      </Link>
                    )}
                    {o.status === "countered" && (
                      <Link to={`/marketplace/listings/${o.listing_id}`}>
                        <Button size="sm" variant="outline" className="rounded-md" data-testid={`offer-view-counter-${o.id}`}>
                          <RotateCcw size={12} className="mr-1" /> Respond
                        </Button>
                      </Link>
                    )}
                    {isPending && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => withdraw(o.id)}
                        disabled={busy === o.id}
                        className="text-red-600 hover:text-red-700 rounded-md"
                        data-testid={`offer-withdraw-${o.id}`}
                      >
                        <X size={13} className="mr-1" /> {busy === o.id ? "…" : "Withdraw"}
                      </Button>
                    )}
                  </div>
                </div>
              </Card>
            );
          })}
        </ul>
      )}
    </div>
  );
}
